import classNames from "classnames";
import React, { useEffect, useState } from "react";
import Dropzone from "react-dropzone";

type AvatarUploaderProps = {
  onSelect?: (base64: string) => void;
  onError?: (message: string) => void;
  uploadSizeLimit?: number;
  resetTrigger?: boolean;
};

export const AvatarUploader = ({
  onSelect,
  onError,
  uploadSizeLimit = 2,
  resetTrigger,
}: AvatarUploaderProps) => {
  const [preview, setPreview] = useState<string | null>(null);

  const handleFile = (file: File) => {
    const reader = new FileReader();
    reader.onload = () => {
      const base64String = reader.result as string;
      setPreview(base64String);
      onSelect?.(base64String);
    };
    reader.readAsDataURL(file);
  };

  useEffect(() => {
    setPreview(null);
  }, [resetTrigger]);

  return (
    <Dropzone
      onDrop={(files) => {
        if (files.length === 0) return;

        const file = files[0];

        if (!["image/jpeg", "image/png"].includes(file.type)) {
          const errorMsg = "Unsupported format. Please upload jpg/png file.";
          onError?.(errorMsg);
          return;
        }

        if (file.size > uploadSizeLimit * 1024 * 1024) {
          const errorMsg = `The file size can't exceed ${uploadSizeLimit}MB. Please upload a smaller image.`;
          onError?.(errorMsg);
          return;
        }

        handleFile(file);
      }}
      accept="image/jpeg, image/png"
      maxSize={uploadSizeLimit * 1024 * 1024}
      className="border-2 border-dashed border-[#E4E4E4] bg-[#FCFCFC] rounded-lg p-6 text-center cursor-pointer hover:border-gray-400 transition-all w-full"
    >
      {(dropzoneProps) => (
        <div onClick={dropzoneProps.open}>
          {preview ? (
            <img
              src={preview}
              alt="Preview"
              className={classNames(
                "w-[80px] h-[80px] lg:w-[60px] lg:h-[60px] mx-auto rounded-full object-cover"
              )}
            />
          ) : (
            <div className="w-[80px] h-[80px] lg:w-[60px] lg:h-[60px] mx-auto rounded-full bg-gray-200 flex items-center justify-center">
              <span className="text-gray-500 text-sm">+</span>
            </div>
          )}
          <p className="mt-2 mb-0 text-grey-lightest text-sm">
            Drop file here, or <span className="underline">browse</span>
          </p>
          <p className="text-grey-lightest text-sm">
            (JPG, PNG, max {uploadSizeLimit}MB)
          </p>
        </div>
      )}
    </Dropzone>
  );
};
